// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';
import classes from '@nerdwallet/base-styles/classes';
import { Title, Text, Box, Accent } from '@nerdwallet/currency';

import {
  boolCheckboxSpec,
  preheaderInput,
  textColorPicker,
  titleInput,
  stringSelectSpec,
  stringTextAreaSpec,
} from '../../lib/editing-configs';
import classColor from '../../lib/parse-color';

// Components
import BackgroundColor from '../background-color/background-color';
import Disclosure, { disclosureTypes } from '../disclosure/disclosure';
import Row from '../row/row';

import './splash.less';

const Splash = ({
  preheader,
  title,
  description,
  descriptionMobile,
  bgOptions,
  textColor,
  disclosureType,
  disclosureColor,
  disclosureOnBackground,
  splashMargins,
  bottomPadding,
  size,
}) => {
  const margins = {
    default: 'default-splash',
    bigger: 'landing-page-narrow__max-widths',
    smaller: 'wide-splash',
  }[splashMargins];

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <Box
          className="splash"
          style={{
            backgroundColor,
            backgroundImage: backgroundColor && 'none',
          }}
        >
          <Row
            className={classNames(margins, 'centered-in-parent')}
            standardMaxWidths={splashMargins === 'default'}
          >
            {disclosureType !== 'none' && (
              <Box className="splash__advertiser-disclosure">
                <Disclosure
                  type={disclosureType}
                  color={disclosureColor}
                  onBackground={disclosureOnBackground}
                />
              </Box>
            )}
            <Box
              className={classNames('splash-header', 'border-after', {
                'squashed-splash': splashMargins === 'bigger',
                'with-disclosure': disclosureType !== 'none',
              })}
            >
              <Box className={classColor(textColor)}>
                <Accent size="small" bold uppercase>
                  {preheader}
                </Accent>
              </Box>
              <Title
                className={classNames([
                  classes('padding-vertical-2'),
                  'splash-title',
                  'type',
                  classColor(textColor),
                ])}
              >
                {title}
              </Title>
              <Box
                className={classNames([
                  'splash-description',
                  'type',
                  classColor(textColor),
                ])}
              >
                <Text>
                  {(size === 'sm' && descriptionMobile) || description}
                </Text>
              </Box>
            </Box>
          </Row>
          {bottomPadding && <Box className={classes('padding-vertical-5')} />}
        </Box>
      )}
    </BackgroundColor>
  );
};

Splash.displayName = 'Splash';

Splash.propTypes = {
  preheader: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  descriptionMobile: PropTypes.string,
  ...BackgroundColor.propTypes,
  textColor: PropTypes.string,
  disclosureType: PropTypes.oneOf([...disclosureTypes, 'none']),
  disclosureColor: PropTypes.string,
  disclosureOnBackground: PropTypes.bool,
  // set splash.maxWidth to 830px, 1264px, or 960px respectively
  splashMargins: PropTypes.oneOf(['bigger', 'smaller', 'default']),
  bottomPadding: PropTypes.bool,
  size: PropTypes.string,
};

Splash.defaultProps = {
  preheader: '',
  title: 'Title goes here',
  description: 'Description (desktop) goes here',
  textColor: 'neutral-darkest',
  splashMargins: 'default',
  bottomPadding: false,
  ...BackgroundColor.defaultProps,
};

Splash.getEditingConfig = (componentDocument) => [
  ...BackgroundColor.getEditingConfig(componentDocument),
  textColorPicker,
  preheaderInput,
  titleInput,
  stringTextAreaSpec('description', 'Subtitle'),
  stringTextAreaSpec('descriptionMobile', 'Mobile subtitle', {
    help: 'Will default to desktop description if no description is provided.',
  }),
  stringSelectSpec(
    'splashMargins',
    'Type margins',
    [
      { value: 'default', label: 'Default' },
      { value: 'bigger', label: 'Bigger' }, // wide-splash
      { value: 'smaller', label: 'Smaller' }, // squashed-splash
    ],
    {
      help: 'Change distance of text from edges of screen.',
    }
  ),
  boolCheckboxSpec('bottomPadding', 'Bottom padding'),
  ...Disclosure.getEditingConfig(),
];

Splash.displayName = 'Splash';

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(Splash);
